import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import type { Company } from '../../services/company-api.js';

interface BuildingData {
  company: Company;
  group: THREE.Group;
  glowMaterials: THREE.Material[];
  baseColor: THREE.Color;
  height: number;
  label: CSS2DObject;
}

@customElement('cp-company-skyline')
export class CpCompanySkyline extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) companies: Company[] = [];

  private _scene!: THREE.Scene;
  private _camera!: THREE.PerspectiveCamera;
  private _renderer!: THREE.WebGLRenderer;
  private _labelRenderer!: CSS2DRenderer;
  private _composer!: EffectComposer;
  private _controls!: OrbitControls;
  private _raycaster = new THREE.Raycaster();
  private _mouse = new THREE.Vector2();
  private _buildings: BuildingData[] = [];
  private _hoveredBuilding: BuildingData | null = null;
  private _cityGroup = new THREE.Group();
  private _particles: THREE.Points | null = null;
  private _container: HTMLDivElement | null = null;
  private _animationId = 0;
  private _resizeObserver: ResizeObserver | null = null;
  private _initialized = false;

  override firstUpdated() {
    const container = this.querySelector('.cp-skyline__container') as HTMLDivElement;
    if (!container || this._initialized) return;
    this._container = container;
    this._initScene(container);
    this._buildCity();
    this._createParticles();
    this._animate();
    this._initialized = true;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._initialized = false;
    cancelAnimationFrame(this._animationId);
    this._resizeObserver?.disconnect();
    this._renderer?.domElement.removeEventListener('click', this._handleClick);
    this._renderer?.domElement.removeEventListener('mousemove', this._handleMouseMove);
    // Remove renderer DOM elements from container to prevent stacking on HMR
    this._renderer?.domElement.remove();
    this._labelRenderer?.domElement.remove();
    this._renderer?.dispose();
    this._composer?.dispose();
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has('companies') && this._initialized) {
      this._rebuildCity();
    }
  }

  /* ──── Scene Setup ──────────────────────────────────────────────── */

  private _initScene(container: HTMLDivElement) {
    // Remove any stale renderer DOM elements (HMR safety)
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const w = container.clientWidth;
    const h = container.clientHeight;

    this._scene = new THREE.Scene();
    this._scene.background = new THREE.Color(0x06061a);
    this._scene.fog = new THREE.FogExp2(0x06061a, 0.008);

    this._camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 200);
    this._camera.position.set(22, 16, 22);

    this._renderer = new THREE.WebGLRenderer({ antialias: true });
    this._renderer.setSize(w, h);
    this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this._renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this._renderer.toneMappingExposure = 1.2;
    container.appendChild(this._renderer.domElement);

    this._labelRenderer = new CSS2DRenderer();
    this._labelRenderer.setSize(w, h);
    this._labelRenderer.domElement.style.position = 'absolute';
    this._labelRenderer.domElement.style.top = '0';
    this._labelRenderer.domElement.style.left = '0';
    this._labelRenderer.domElement.style.pointerEvents = 'none';
    container.appendChild(this._labelRenderer.domElement);

    // Bloom - stronger for neon glow
    this._composer = new EffectComposer(this._renderer);
    this._composer.addPass(new RenderPass(this._scene, this._camera));
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      1.4,   // strength (was 0.7)
      0.6,   // radius (was 0.4)
      0.15,  // threshold - low so windows glow (was 0.82)
    );
    this._composer.addPass(bloom);
    this._composer.addPass(new OutputPass());

    // OrbitControls
    this._controls = new OrbitControls(this._camera, this._renderer.domElement);
    this._controls.enableDamping = true;
    this._controls.dampingFactor = 0.05;
    this._controls.maxPolarAngle = Math.PI / 2.1;
    this._controls.minDistance = 8;
    this._controls.maxDistance = 50;
    this._controls.target.set(0, 3, 0);

    // Lighting
    this._scene.add(new THREE.AmbientLight(0x1a1a3e, 0.3));
    const dirLight = new THREE.DirectionalLight(0x2244aa, 0.4);
    dirLight.position.set(10, 25, 10);
    this._scene.add(dirLight);

    this._createGround();
    this._scene.add(this._cityGroup);

    this._renderer.domElement.addEventListener('click', this._handleClick);
    this._renderer.domElement.addEventListener('mousemove', this._handleMouseMove);

    this._resizeObserver = new ResizeObserver(() => this._handleResize(container));
    this._resizeObserver.observe(container);
  }

  private _createGround() {
    // Bright grid
    const grid = new THREE.GridHelper(80, 80, 0x1a4a6a, 0x0a1a2a);
    grid.position.y = -0.01;
    this._scene.add(grid);

    // Ground plane
    const geo = new THREE.PlaneGeometry(80, 80);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x060618,
      roughness: 0.7,
      metalness: 0.3,
    });
    const ground = new THREE.Mesh(geo, mat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.02;
    this._scene.add(ground);
  }

  /* ──── Particles ────────────────────────────────────────────────── */

  private _createParticles() {
    const count = 300;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 50;
      positions[i * 3 + 1] = Math.random() * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 50;

      colors[i * 3] = 0.3 + Math.random() * 0.4;
      colors[i * 3 + 1] = 0.4 + Math.random() * 0.4;
      colors[i * 3 + 2] = 0.7 + Math.random() * 0.3;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.PointsMaterial({
      size: 0.06,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this._particles = new THREE.Points(geo, mat);
    this._scene.add(this._particles);
  }

  /* ──── Building Generation ──────────────────────────────────────── */

  private _buildCity() {
    const count = this.companies.length;
    if (count === 0) return;

    const cols = Math.ceil(Math.sqrt(count));
    const spacing = 9;
    const offsetX = ((cols - 1) * spacing) / 2;
    const offsetZ = ((Math.ceil(count / cols) - 1) * spacing) / 2;

    this.companies.forEach((company, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = col * spacing - offsetX;
      const z = row * spacing - offsetZ;
      this._createBuilding(company, x, z);
    });

    this._controls.target.set(0, 3, 0);
    this._controls.update();
  }

  private _createBuilding(company: Company, x: number, z: number) {
    const group = new THREE.Group();
    const baseColor = new THREE.Color(company.color ?? '#4488ff');
    const glowMaterials: THREE.Material[] = [];

    // Deterministic variety
    const seed = (company.id * 2654435761) >>> 0;
    const h1 = (seed & 0xff) / 255;
    const h2 = ((seed >> 8) & 0xff) / 255;
    const h3 = ((seed >> 16) & 0xff) / 255;

    const width = 1.8 + h2 * 1.2;
    const depth = 1.8 + h3 * 1.2;
    const height = 4 + h1 * 8;

    // ── Glass body ──
    const bodyGeo = new THREE.BoxGeometry(width, height, depth);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: baseColor.clone().multiplyScalar(0.15),
      emissive: baseColor.clone().multiplyScalar(0.03),
      metalness: 0.9,
      roughness: 0.15,
      transparent: true,
      opacity: 0.5,
    });
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.set(0, height / 2, 0);
    body.userData = { companyId: company.id };
    group.add(body);

    // ── Neon edges (outer frame) ──
    const edgesGeo = new THREE.EdgesGeometry(bodyGeo);
    const edgeMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone().multiplyScalar(3),
    });
    glowMaterials.push(edgeMat);
    // Use LineSegments but with LineBasicMaterial for compatibility
    const edgeLineMat = new THREE.LineBasicMaterial({
      color: baseColor.clone().multiplyScalar(3),
    });
    glowMaterials.push(edgeLineMat);
    const edges = new THREE.LineSegments(edgesGeo, edgeLineMat);
    edges.position.copy(body.position);
    group.add(edges);

    // ── Glowing window panels ──
    this._addWindows(group, width, height, depth, baseColor, seed, glowMaterials);

    // ── Floor separator lines ──
    const floors = Math.max(2, Math.floor(height / 1.6));
    const floorMat = new THREE.LineBasicMaterial({
      color: baseColor.clone().multiplyScalar(1.8),
      transparent: true,
      opacity: 0.5,
    });
    glowMaterials.push(floorMat);

    const hw = width / 2;
    const hd = depth / 2;
    for (let f = 1; f < floors; f++) {
      const y = f * (height / floors);
      const pts = [
        new THREE.Vector3(-hw, y, -hd),
        new THREE.Vector3(hw, y, -hd),
        new THREE.Vector3(hw, y, hd),
        new THREE.Vector3(-hw, y, hd),
        new THREE.Vector3(-hw, y, -hd),
      ];
      const lineGeo = new THREE.BufferGeometry().setFromPoints(pts);
      const line = new THREE.Line(lineGeo, floorMat);
      group.add(line);
    }

    // ── Base glow ring ──
    const ringRadius = Math.max(width, depth) * 0.7;
    const ringGeo = new THREE.RingGeometry(ringRadius - 0.15, ringRadius + 0.15, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone().multiplyScalar(2.5),
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
    });
    glowMaterials.push(ringMat);
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.03;
    group.add(ring);

    // ── Base glow disc (soft light on ground) ──
    const discGeo = new THREE.CircleGeometry(ringRadius + 0.5, 32);
    const discMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone().multiplyScalar(1),
      transparent: true,
      opacity: 0.15,
      side: THREE.DoubleSide,
    });
    const disc = new THREE.Mesh(discGeo, discMat);
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.02;
    group.add(disc);

    // ── Point light at top ──
    const pointLight = new THREE.PointLight(baseColor, 0.8, 12);
    pointLight.position.set(0, height + 0.5, 0);
    group.add(pointLight);

    // ── Small accent light at base ──
    const baseLt = new THREE.PointLight(baseColor, 0.4, 6);
    baseLt.position.set(0, 0.5, 0);
    group.add(baseLt);

    // ── CSS2D Label (hidden by default, shown on hover) ──
    const labelDiv = document.createElement('div');
    labelDiv.className = 'cp-skyline__label';
    labelDiv.textContent = company.name;
    const brightHex = baseColor.clone().multiplyScalar(2).getHexString();
    labelDiv.style.setProperty('--label-color', `#${brightHex}`);
    labelDiv.style.display = 'none';
    const label = new CSS2DObject(labelDiv);
    label.position.set(0, height + 1.5, 0);
    group.add(label);

    group.position.set(x, 0, z);
    this._cityGroup.add(group);

    this._buildings.push({ company, group, glowMaterials, baseColor, height, label });
  }

  private _addWindows(
    group: THREE.Group,
    buildingW: number,
    buildingH: number,
    buildingD: number,
    baseColor: THREE.Color,
    seed: number,
    glowMaterials: THREE.Material[],
  ) {
    const winSize = 0.28;
    const winGap = 0.18;
    const margin = 0.35;

    // Lit window material (glows via bloom)
    const litMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone().multiplyScalar(4),
      transparent: true,
      opacity: 0.85,
    });
    glowMaterials.push(litMat);

    // Dim window material
    const dimMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone().multiplyScalar(0.5),
      transparent: true,
      opacity: 0.2,
    });

    const winGeo = new THREE.PlaneGeometry(winSize, winSize);

    // 4 faces: front (+z), back (-z), right (+x), left (-x)
    const faces: Array<{
      facew: number;
      normal: THREE.Vector3;
      right: THREE.Vector3;
      offset: number;
    }> = [
      { facew: buildingW, normal: new THREE.Vector3(0, 0, 1), right: new THREE.Vector3(1, 0, 0), offset: buildingD / 2 + 0.01 },
      { facew: buildingW, normal: new THREE.Vector3(0, 0, -1), right: new THREE.Vector3(-1, 0, 0), offset: buildingD / 2 + 0.01 },
      { facew: buildingD, normal: new THREE.Vector3(1, 0, 0), right: new THREE.Vector3(0, 0, -1), offset: buildingW / 2 + 0.01 },
      { facew: buildingD, normal: new THREE.Vector3(-1, 0, 0), right: new THREE.Vector3(0, 0, 1), offset: buildingW / 2 + 0.01 },
    ];

    faces.forEach((face, fi) => {
      const cols = Math.max(1, Math.floor((face.facew - 2 * margin) / (winSize + winGap)));
      const rows = Math.max(1, Math.floor((buildingH - 2 * margin) / (winSize + winGap)));

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          // Deterministic lit pattern
          const wSeed = ((seed + fi * 997 + r * 131 + c * 17) * 2654435761) >>> 0;
          const isLit = (wSeed & 0xff) > 80; // ~68% windows lit

          const cx = (c - (cols - 1) / 2) * (winSize + winGap);
          const cy = margin + r * (winSize + winGap) + winSize / 2;

          const pos = face.normal.clone().multiplyScalar(face.offset);
          pos.add(face.right.clone().multiplyScalar(cx));
          pos.y = cy;

          const win = new THREE.Mesh(winGeo, isLit ? litMat : dimMat);
          win.position.copy(pos);
          win.lookAt(pos.clone().add(face.normal));
          group.add(win);
        }
      }
    });
  }

  private _rebuildCity() {
    this._cityGroup.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
      }
      if (obj instanceof THREE.LineSegments || obj instanceof THREE.Line) {
        obj.geometry.dispose();
      }
    });
    // Dispose materials from building data
    this._buildings.forEach((b) => {
      b.glowMaterials.forEach((m) => m.dispose());
    });
    this._cityGroup.clear();
    this._buildings = [];
    this._hoveredBuilding = null;
    this._buildCity();
  }

  /* ──── Animation Loop ───────────────────────────────────────────── */

  private _animate = () => {
    this._animationId = requestAnimationFrame(this._animate);
    this._controls.update();

    const time = performance.now() * 0.001;

    // Neon pulse on edges
    this._buildings.forEach((b, i) => {
      const pulse = 0.8 + Math.sin(time * 1.5 + i * 0.9) * 0.2;
      const isHovered = this._hoveredBuilding === b;
      const boost = isHovered ? 1.8 : 1.0;

      b.glowMaterials.forEach((mat) => {
        if ('color' in mat && mat.color instanceof THREE.Color) {
          (mat as THREE.MeshBasicMaterial).color.copy(b.baseColor).multiplyScalar((2.5 + pulse) * boost);
        }
      });
    });

    // Animate particles (slow upward drift)
    if (this._particles) {
      const pos = this._particles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < pos.length; i += 3) {
        pos[i + 1] += 0.003 + Math.sin(time + i) * 0.001;
        if (pos[i + 1] > 20) pos[i + 1] = 0;
        pos[i] += Math.sin(time * 0.5 + i * 0.1) * 0.002;
      }
      this._particles.geometry.attributes.position.needsUpdate = true;
    }

    this._composer.render();
    this._labelRenderer.render(this._scene, this._camera);
  };

  /* ──── Event Handlers ───────────────────────────────────────────── */

  private _handleResize(container: HTMLDivElement) {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    this._camera.aspect = w / h;
    this._camera.updateProjectionMatrix();
    this._renderer.setSize(w, h);
    this._labelRenderer.setSize(w, h);
    this._composer.setSize(w, h);
  }

  private _getMeshes(): THREE.Mesh[] {
    return this._buildings
      .map((b) => b.group.children.find(
        (c): c is THREE.Mesh => c instanceof THREE.Mesh && 'companyId' in c.userData,
      )!)
      .filter(Boolean);
  }

  private _handleMouseMove = (e: MouseEvent) => {
    const rect = this._renderer.domElement.getBoundingClientRect();
    this._mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this._mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this._raycaster.setFromCamera(this._mouse, this._camera);
    const intersects = this._raycaster.intersectObjects(this._getMeshes());

    // Hide previous label
    if (this._hoveredBuilding) {
      (this._hoveredBuilding.label.element as HTMLElement).style.display = 'none';
      this._renderer.domElement.style.cursor = 'default';
      this._hoveredBuilding = null;
    }

    if (intersects.length > 0) {
      const hit = intersects[0].object;
      const building = this._buildings.find((b) => b.group.children.includes(hit));
      if (building) {
        this._hoveredBuilding = building;
        (building.label.element as HTMLElement).style.display = '';
        this._renderer.domElement.style.cursor = 'pointer';
      }
    }
  };

  private _handleClick = (e: MouseEvent) => {
    const rect = this._renderer.domElement.getBoundingClientRect();
    this._mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this._mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this._raycaster.setFromCamera(this._mouse, this._camera);
    const intersects = this._raycaster.intersectObjects(this._getMeshes());

    if (intersects.length > 0) {
      const companyId = intersects[0].object.userData.companyId as number;
      if (companyId) {
        this.dispatchEvent(new CustomEvent('company-selected', {
          detail: { companyId },
          bubbles: true,
          composed: true,
        }));
      }
    }
  };

  /* ──── Render ────────────────────────────────────────────────────── */

  override render() {
    return html`<div class="cp-skyline__container"></div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-skyline': CpCompanySkyline;
  }
}
